import React, { useState } from "react";
import { useLanguage } from "@/App";
import { ArrowRight, ExternalLink, Layers, Zap } from "lucide-react";
import { motion } from "framer-motion";

const Showcase3D = () => {
  const { language } = useLanguage();
  const [hoveredItem, setHoveredItem] = useState(null);

  const showcaseItems = [
    {
      id: 1,
      title_en: "Luxury E-Commerce Platform",
      title_nl: "Luxe E-Commerce Platform",
      description_en: "High-end fashion store with 3D product visualization and AI recommendations",
      description_nl: "High-end fashion winkel met 3D visualisatie en AI aanbevelingen",
      category: "E-commerce",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80",
      tags: ["React", "3D", "AI"],
      gradient: "from-orange-500 to-red-600",
      featured: true
    },
    {
      id: 2,
      title_en: "AI Analytics Dashboard",
      title_nl: "AI Analytics Dashboard",
      description_en: "Real-time data visualization with predictive AI insights",
      description_nl: "Real-time data visualisatie met voorspellende AI inzichten",
      category: "SaaS Dashboard",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80",
      tags: ["AI", "Dashboard", "Analytics"],
      gradient: "from-purple-500 to-indigo-600",
      featured: true
    },
    {
      id: 3,
      title_en: "3D Product Configurator",
      title_nl: "3D Product Configurator",
      description_en: "Interactive WebGL configurator with real-time rendering",
      description_nl: "Interactieve WebGL configurator met real-time rendering",
      category: "WebGL",
      image: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&q=80",
      tags: ["3D", "WebGL"],
      gradient: "from-cyan-500 to-blue-600",
      featured: false
    },
    {
      id: 4,
      title_en: "Fintech Platform",
      title_nl: "Fintech Platform",
      description_en: "Trading interface with live market data",
      description_nl: "Trading interface met live marktdata",
      category: "Fintech",
      image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80",
      tags: ["Fintech", "Real-time"],
      gradient: "from-emerald-500 to-teal-600",
      featured: false
    },
    {
      id: 5,
      title_en: "Creative Portfolio",
      title_nl: "Creatief Portfolio",
      description_en: "Award-winning portfolio with animations",
      description_nl: "Bekroond portfolio met animaties",
      category: "Portfolio",
      image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&q=80",
      tags: ["Animation", "Creative"],
      gradient: "from-amber-500 to-orange-600",
      featured: false
    },
    {
      id: 6,
      title_en: "AI Automation Platform",
      title_nl: "AI Automatisering Platform",
      description_en: "Workflow automation with intelligent AI",
      description_nl: "Workflow automatisering met intelligente AI",
      category: "AI",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80",
      tags: ["AI", "Automation"],
      gradient: "from-pink-500 to-rose-600",
      featured: false
    }
  ];

  const getTitle = (item) => language === "nl" ? item.title_nl : item.title_en;
  const getDescription = (item) => language === "nl" ? item.description_nl : item.description_en;

  return (
    <section
      id="showcase"
      data-testid="showcase-section"
      className="py-32 bg-[#030303] relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,107,0,0.05),transparent_70%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="inline-block text-[#FF6B00] text-xs font-bold uppercase tracking-[0.3em] mb-4">
            {language === "nl" ? "Ons Portfolio" : "Our Portfolio"}
          </span>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {language === "nl" ? "Wat Wij" : "What We"}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] to-[#FF4500]">
              {language === "nl" ? "Bouwen" : "Build"}
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            {language === "nl" 
              ? "Van AI-platforms tot 3D web ervaringen. Ontdek wat wij voor jou kunnen realiseren."
              : "From AI platforms to 3D web experiences. Discover what we can build for you."}
          </p>
        </motion.div>

        {/* Featured Projects */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {showcaseItems.filter(item => item.featured).map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              onMouseEnter={() => setHoveredItem(item.id)}
              onMouseLeave={() => setHoveredItem(null)}
              className="group relative"
            >
              {/* Glow effect */}
              <div className={`absolute -inset-1 bg-gradient-to-r ${item.gradient} opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500`} />
              
              {/* Card */}
              <div className="relative bg-[#0A0A0A] border border-white/5 overflow-hidden group-hover:border-[#FF6B00]/30 transition-all">
                {/* Image */}
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={item.image}
                    alt={getTitle(item)}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/40 to-transparent" />
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                    <div className="text-center">
                      <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-r ${item.gradient} rounded-full flex items-center justify-center`}>
                        <Layers size={28} className="text-white" />
                      </div>
                      <p className="text-white font-bold text-lg">
                        {language === "nl" ? "Bekijk Project" : "View Project"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {item.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-[#FF6B00]/10 text-[#FF6B00] text-xs font-bold uppercase tracking-wider"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {getTitle(item)}
                  </h3>
                  <p className="text-zinc-400">{getDescription(item)}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Smaller Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {showcaseItems.filter(item => !item.featured).map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="group relative"
            >
              <div className="relative bg-[#0A0A0A] border border-white/5 overflow-hidden group-hover:border-[#FF6B00]/30 transition-all">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={item.image}
                    alt={getTitle(item)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
                  
                  {/* Hover icon */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                    <div className={`w-12 h-12 bg-gradient-to-r ${item.gradient} rounded-full flex items-center justify-center`}>
                      <ExternalLink size={20} className="text-white" />
                    </div>
                  </div>
                </div>
                
                <div className="p-4">
                  <span className="text-[#FF6B00] text-xs font-bold uppercase tracking-wider">{item.category}</span>
                  <h3 className="text-white font-bold mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {getTitle(item)}
                  </h3>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <a
            href="#contact"
            className="group inline-flex items-center gap-3 bg-gradient-to-r from-[#FF6B00] to-[#FF4500] text-white px-10 py-5 text-sm font-bold tracking-wide hover:shadow-[0_0_40px_rgba(255,107,0,0.3)] transition-all"
          >
            {language === "nl" ? "Bespreek Jouw Project" : "Discuss Your Project"}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Showcase3D;
